// packages/shared/src/column-scripts.ts
//
// "Does this column have a script to run, and may it run?" — asked identically
// by the grid, exports, views, visualizations, search and validation.
//
// A column carries two scripts (`script`, which computes what the cell shows,
// and `validate`, which vetoes a manual edit) and each has a switch beside it
// (`scriptActive`, `validateActive`). ABSENT MEANS ON, so every column written
// before the switch existed keeps running its script.
//
// The whole point of putting this here is that the switch is worthless if one
// reader forgets it. A caller that writes `col.script?.trim()` gets a script the
// user switched off, silently — the column editor says red and the grid runs it
// anyway. So the rule is: nothing reads `script` / `validate` directly; every
// reader asks one of these two, and gets back the source only when it should
// actually run.
//
// They live in shared rather than the renderer because `edb-store` and the
// Electron main process see the same ColumnSpec, and a second copy of a
// default-on rule is exactly the kind of thing that drifts to default-off.
/**
 * The render script to run for this column, or `undefined` when there is none
 * to run — either because none is set or because the user switched it off.
 *
 * Blank and whitespace-only bodies count as "none": that is what the column
 * editor stores when the script is cleared, and running one would report a
 * "no script" error into every cell.
 */
export function activeColumnScript(col) {
    if (!col || col.scriptActive === false)
        return undefined;
    return col.script?.trim() ? col.script : undefined;
}
/** The validation rule to run for this column, on the same terms. */
export function activeValidateScript(col) {
    if (!col || col.validateActive === false)
        return undefined;
    return col.validate?.trim() ? col.validate : undefined;
}
/**
 * Did the script decline to answer?
 *
 * `render(row)` returning `null` — or falling off the end, which returns
 * `undefined` — means "I have nothing to say about this row", and the column
 * shows its STORED value instead. That is what makes a scripted column editable:
 * a script that decorates only the rows it recognises leaves the others as
 * ordinary cells, which the user can type into, and the script can then read what
 * they typed on the next render.
 *
 * An empty string is NOT a decline. `return ''` is an answer — "show nothing
 * here" — and a script that computes a label for some rows and deliberately
 * blanks it for others has to be able to say so.
 */
export function scriptDeclined(value) {
    return value === null || value === undefined;
}
/** Which of the three a body-plus-switch pair is in. */
export function scriptState(src, active) {
    if (!src?.trim())
        return 'none';
    return active === false ? 'off' : 'on';
}
//# sourceMappingURL=column-scripts.js.map